import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API } from "@/services/API";

const initialState = {
  loads: {
    data: null,
    loading: false,
    error: null,
  },
};

// Flatten EnrichedLoadBoardItem response: spread loadBoardItem properties and add routeAnalysis
const flattenEnrichedLoads = (enrichedLoads) =>
  enrichedLoads.map((item) => ({
    ...item.loadBoardItem,
    routeAnalysis: item.routeAnalysis,
  }));

export const fetchLoads = createAsyncThunk(
  "loadSearch/fetch-loads",
  async (
    { trailerType, pickup, delivery, pickupDate, pickupDateEnd, deliveryDate, deliveryDateEnd, pickupRadius, deliveryRadius },
    { rejectWithValue },
  ) => {
    return API.get("/parade/get-loads", {
      params: {
        "earliest-pickup-date": pickupDate,
        "latest-pickup-date": pickupDateEnd || pickupDate,
        "earliest-delivery-date": deliveryDate,
        "latest-delivery-date": deliveryDateEnd || deliveryDate,
        trailer: trailerType,
        "pickup-zip": pickup.zip,
        "delivery-zip": delivery?.zip,
        "pickup-radius": pickupRadius ? parseInt(pickupRadius) : null,
        "delivery-radius": deliveryRadius ? parseInt(deliveryRadius) : null,
      },
    })
      .then((res) => {
        const loads = flattenEnrichedLoads(res);
        return loads.sort((a, b) => ((a.updatedAt || a.createdAt) < (b.updatedAt || b.createdAt) ? 1 : -1));
      })
      .catch((err) => {
        console.error("Failed to fetch loads data", err);
        return rejectWithValue(err);
      });
  },
);

export const refreshLoads = createAsyncThunk(
  "loadSearch/refresh-loads",
  async (
    { trailerType, pickup, delivery, pickupDate, pickupDateEnd, deliveryDate, deliveryDateEnd, pickupRadius, deliveryRadius },
    { rejectWithValue },
  ) => {
    return API.get("/parade/get-loads", {
      params: {
        "earliest-pickup-date": pickupDate,
        "latest-pickup-date": pickupDateEnd || pickupDate,
        "earliest-delivery-date": deliveryDate,
        "latest-delivery-date": deliveryDateEnd || deliveryDate,
        trailer: trailerType,
        "pickup-zip": pickup.zip,
        "delivery-zip": delivery?.zip,
        "pickup-radius": pickupRadius ? parseInt(pickupRadius) : null,
        "delivery-radius": deliveryRadius ? parseInt(deliveryRadius) : null,
      },
    })
      .then((res) => {
        const loads = flattenEnrichedLoads(res);
        return loads.sort((a, b) => ((a.updatedAt || a.createdAt) < (b.updatedAt || b.createdAt) ? 1 : -1));
      })
      .catch((err) => {
        console.error("Failed to refresh loads data", err);
        return rejectWithValue(err);
      });
  },
);

const loadSearchSlice = createSlice({
  name: "loadSearch",
  initialState,
  reducers: {
    clearLoadSearchState: () => {
      return initialState;
    },
    markLoadAsUnavailable: (state, action) => {
      const loadUniqueId = action.payload;
      if (state.loads.data) {
        const load = state.loads.data.find((load) => load.uniqueId === loadUniqueId);
        load.isUnavailable = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLoads.pending, (state) => {
        state.loads = {
          data: null,
          loading: true,
          error: null,
        };
      })
      .addCase(fetchLoads.fulfilled, (state, action) => {
        state.loads = {
          data: action.payload.map((load) => ({ ...load, isUnavailable: false })),
          loading: false,
          error: null,
        };
      })
      .addCase(fetchLoads.rejected, (state, action) => {
        state.loads = {
          data: null,
          loading: false,
          error: action.payload || "Error fetching loads data",
        };
      })
      .addCase(refreshLoads.fulfilled, (state, action) => {
        const newLoads = action.payload;
        const currentLoads = state.loads.data || [];

        // Create a Set of new load IDs for fast lookup
        const newLoadIds = new Set(newLoads.map((load) => load.uniqueId));

        // Mark current loads as unavailable if they're not in the new loads
        const updatedCurrentLoads = currentLoads.map((load) => ({
          ...load,
          isUnavailable: !newLoadIds.has(load.uniqueId),
        }));

        // Create a Set of current load IDs
        const currentLoadIds = new Set(currentLoads.map((load) => load.uniqueId));

        // Add new loads that weren't in the current loads
        const freshLoads = newLoads.filter((load) => !currentLoadIds.has(load.uniqueId)).map((load) => ({ ...load, isUnavailable: false }));

        // Merge: keep existing loads (some marked unavailable) and add fresh loads
        const mergedLoads = [...updatedCurrentLoads, ...freshLoads].sort((a, b) =>
          (a.updatedAt || a.createdAt) < (b.updatedAt || b.createdAt) ? 1 : -1,
        );

        state.loads = {
          ...state.loads,
          data: mergedLoads,
        };
      })
      .addCase(refreshLoads.rejected, (state, action) => {
        // Don't clear data on refresh error, just log it
        console.error("Failed to refresh loads", action.payload);
      });
  },
});

export const { clearLoadSearchState, markLoadAsUnavailable } = loadSearchSlice.actions;

export default loadSearchSlice.reducer;
